import React, { useEffect, useRef } from "react";
import { useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import algoliasearch from "algoliasearch";
import { useStaticQuery, graphql } from "gatsby";

import "./DocSearch.scss";

const searchClient = algoliasearch(process.env.ALGOLIA_APP_ID, process.env.ALGOLIA_ADMIN_KEY);
const docsIndex = searchClient.initIndex(
	process.env.NODE_ENV === "production" ? "Docs" : "DocsDev",
);

function pageToAlgoliaRecord({ node: { id, frontmatter, htmlAst, ...rest } }) {
	const h2Childrens = htmlAst.children.filter((ch) => ch.tagName === "h2");
	const sections = h2Childrens.map((ch) => ch.properties.id);
	return {
		objectID: id,
		sections: sections,
		...frontmatter,
		...rest,
	};
}

interface DocSearchProps {
	style?: any;
	isMobile: boolean;
}

const DocSearch = (props: DocSearchProps) => {
	const [isOpenMobile, setIsOpenMobile] = useState(false);
	const [isDropDownOpen, setIsDropDownOpen] = useState(false);
	const [value, setValue] = useState("");
	const [hitsApi, setHitsApi] = useState([]);
	const [hitsDocs, setHitsDocs] = useState([]);
	const ref = useRef(null);

	useEffect(() => {
		function handleClickOutside(event) {
			if (ref.current && !ref.current.contains(event.target)) {
				setIsDropDownOpen(!isDropDownOpen);
			}
		}
		if (isDropDownOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}
	}, [ref, isDropDownOpen]);

	const onChange = (value) => {
		setValue(value);
		if (value.length > 2) {
			docsIndex.search(value).then(({ hits }: any) => {
				setHitsApi(hits.filter((h) => h.category === "api"));
				setHitsDocs(hits.filter((h) => h.category === "documentation"));
			});
		}
	};

	const onFocus = (data: any) => {
		const docsAlgolia = data.pages.edges.map(pageToAlgoliaRecord);
		docsIndex.replaceAllObjects(docsAlgolia, { autoGenerateObjectIDIfNotExist: true });
		setIsDropDownOpen(true);
	};

	const searchHitsElements = (hits) => {
		return hits.map((h) => (
			<li className="docs" key={h.objectID}>
				<a
					href={"/" + h.category + "/" + h.platform + h.path}
					className="flex-container align-items-center"
					onClick={() => setIsDropDownOpen(false)}
				>
					<div className="data-holder">
						<p>{h.title}</p>
						<span>{`${h.category} > ${h.platform} ${h.group ? ">" + h.group : ""}`}</span>
					</div>
				</a>
			</li>
		));
	};

	const onCloseButton = () => {
		setIsDropDownOpen(false);
		setValue("");
	};

	const valueLength = value.length;
	const data = useStaticQuery(graphql`
		query AlgoliQuery {
			pages: allMarkdownRemark(filter: { fileAbsolutePath: {} }) {
				edges {
					node {
						id
						frontmatter {
							path
							title
							category
							platform
							group
						}
						htmlAst
						excerpt(pruneLength: 5000)
					}
				}
			}
		}
	`);
	const hitsApiLength = hitsApi.length;
	const hitsDocsLength = hitsDocs.length;
	const closeButton = require("../../../res/close_button_icon.svg").default;
	const searchStyle = { ...props.style };
	if (props.isMobile) {
		searchStyle.display = isOpenMobile ? "flex" : "none";
	}

	return (
		<>
			{props.isMobile ? (
				<span className="search-icon" onClick={() => setIsOpenMobile(!isOpenMobile)}>
					<i className="fa-regular fa-search" />
				</span>
			) : null}

			<div style={searchStyle} className="general-search" ref={ref}>
				{props.isMobile ? (
					<span className="search-back" onClick={() => setIsOpenMobile(!isOpenMobile)}>
						<i className="fa-light fa-chevron-left" />
					</span>
				) : null}
				<SearchIcon style={{ width: 25, height: 25, cursor: "pointer" }} />
				<input
					type="text"
					placeholder="Search..."
					id="search-box"
					onClick={() => onFocus(data)}
					onChange={(e) => onChange(e.target.value)}
					value={value}
				/>
				{valueLength > 0 ? (
					<span className="close-button" onClick={() => onCloseButton()}>
						<img src={closeButton} />
					</span>
				) : null}

				{valueLength > 2 && isDropDownOpen ? (
					<div className="initial-search-results search-container">
						<ul>
							{hitsApiLength > 0 ? (
								<li className="docs results-header hide">API Reference</li>
							) : null}
							{searchHitsElements(hitsApi)}
							{hitsDocsLength > 0 ? (
								<li className="docs results-header hide">Documentation</li>
							) : null}
							{searchHitsElements(hitsDocs)}
							{hitsApiLength === 0 && hitsDocsLength === 0 ? (
								<li className="no-results results-header">No results found</li>
							) : null}
						</ul>
					</div>
				) : null}
			</div>
		</>
	);
};

export default DocSearch;
